/**
 * Gold Price API service for fetching current gold prices from external API
 */

import { useQuery } from '@tanstack/react-query'
import type { GoldPriceApiResponse, GoldType } from '@/types/price'

const GOLD_PRICE_API_URL =
  import.meta.env.VITE_GOLD_PRICE_API_URL ||
  'https://sjc.com.vn/GoldPrice/Services/PriceService.ashx'

/**
 * Fetch current gold prices from external API
 */
export async function fetchGoldPrices(branchName: string = 'Hồ Chí Minh'): Promise<GoldType[]> {
  const today = new Date()
  const dateStr = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`

  // Try GET method first (as API might support it directly)
  // If that fails, fall back to POST
  let response: Response
  let data: GoldPriceApiResponse

  try {
    // Try GET method with query parameters
    const url = new URL(GOLD_PRICE_API_URL)
    url.searchParams.append('method', 'GetSJCGoldPriceByDate')
    url.searchParams.append('toDate', dateStr)
    
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`GET request failed: ${response.status}`)
    }

    data = await response.json()
  } catch (getError) {
    // Fallback to POST method
    const formData = new URLSearchParams()
    formData.append('method', 'GetSJCGoldPriceByDate')
    formData.append('toDate', dateStr)

    response = await fetch(GOLD_PRICE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch gold prices: ${response.status} ${response.statusText}`)
    }

    data = await response.json()
  }

  if (!data.success) {
    throw new Error('API returned success=false')
  }

  if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
    throw new Error('API returned empty data array')
  }

  // Filter by branch name and return unique gold types
  const branchPrices = data.data.filter((price) => price.BranchName === branchName)
  
  if (branchPrices.length === 0) {
    console.warn(`No gold types found for branch: ${branchName}. Available branches:`, 
      [...new Set(data.data.map(p => p.BranchName))])
  }
  
  // Group by gold type ID to get unique types
  const uniqueTypes = new Map<number, GoldType>()
  branchPrices.forEach((price) => {
    if (!uniqueTypes.has(price.Id)) {
      uniqueTypes.set(price.Id, price)
    }
  })

  const result = Array.from(uniqueTypes.values())
  console.log(`Fetched ${result.length} unique gold types for branch ${branchName}`, result.map(t => ({ Id: t.Id, TypeName: t.TypeName })))
  
  return result
}

/**
 * React Query hook for fetching gold prices
 */
export function useGoldPrices(branchName: string = 'Hồ Chí Minh') {
  return useQuery({
    queryKey: ['goldPrices', branchName],
    queryFn: () => fetchGoldPrices(branchName),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })
}

